import { useState, useEffect } from 'react';

// Module-level cache to track which images have been loaded this session
// Prevents flash on component remount (e.g., tab switching)
const loadedImages = new Set<string>();

/**
 * Hook to preload an image and track its loading state
 * Returns true when the image has loaded, false otherwise
 *
 * Caches loaded status at module level to prevent flash on remount.
 *
 * @param src - The image source URL to preload
 * @returns boolean indicating if the image has loaded
 */
export function useImagePreload(src: string): boolean {
  // Initialize as true if we've already loaded this image this session
  const [isLoaded, setIsLoaded] = useState(() => !src || loadedImages.has(src));

  useEffect(() => {
    // If src is empty or already loaded, nothing to do
    if (!src || loadedImages.has(src)) {
      setIsLoaded(true);
      return;
    }

    const img = new Image();
    img.onload = () => {
      loadedImages.add(src);
      setIsLoaded(true);
    };
    img.onerror = () => {
      // If image fails to load, still mark as loaded to avoid blocking forever
      loadedImages.add(src);
      setIsLoaded(true);
    };
    img.src = src;

    // Check if image is already cached (complete will be true if cached)
    if (img.complete) {
      loadedImages.add(src);
      setIsLoaded(true);
    }
  }, [src]);

  return isLoaded;
}
